

import React, { useContext } from "react";
import { LoadingContext, ErrorContext } from "/src/helpers/contexts/agentContext";
import { useNavigate } from "react-router";
export function useApi() {
    const [currentLoading, setCurrentLoading] = useContext(LoadingContext);
    const [currentError, setCurrentError] = useContext(ErrorContext);
    const navigate = useNavigate();

    const get = async (path, queryString, hideLoading = false, message = "Loading...") => {
        let postsData = null;
        if (!hideLoading) {
            setCurrentLoading(true, message);
        }
        try {

            const response = await fetch(
                `${path}?${queryString}`
            );
            if (!response.ok) {
                if (response.status === 401) {
                    if (!hideLoading) {
                        setCurrentLoading(false);
                    }
                    navigate("/login");
                    return;
                }
                setCurrentError(`API Error: ${response.status} on ${path}?${queryString}`);
            }
            postsData = await response.json();

        } catch (parseError) {
            console.log("error", JSON.stringify(parseError));
            setCurrentError(parseError.message);

        }
        if (!hideLoading) {
            setCurrentLoading(false);
        }

        return postsData;
    }

    const post = async (path, queryString, data, hideLoading = false, message = "Loading...", handleError = true) => {
        if (!hideLoading) {
            setCurrentLoading(true, message);
        }
        try {
            const response = await fetch(
                `${path}?${queryString}`,
                {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    method: "POST",
                    body: JSON.stringify(data)
                }
            );
            if (!response.ok && handleError) {
                if (response.status === 401) {
                    navigate("/login");
                    return;
                }
                setCurrentError(`API Error: ${response.status} on ${path}?${queryString}`);
            }
            // Error responses aren't always JSON (e.g. dev exception pages) — never let the
            // parse throw past us or the loading overlay gets stuck.
            try { return await response.json(); } catch { return null; }
        } catch (err) {
            if (handleError) setCurrentError(err.message);
            return null;
        } finally {
            if (!hideLoading) {
                setCurrentLoading(false);
            }
        }
    }
    // Verb-flexible sender that surfaces the status code — basket endpoints answer 409 for
    // business conflicts (day already taken, quote expired) that the UI handles itself.
    const send = async (method, path, data, message = null) => {
        if (message) setCurrentLoading(true, message);
        try {
            const response = await fetch(path, {
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                method,
                body: data === undefined ? undefined : JSON.stringify(data)
            });
            if (response.status === 401) { navigate("/login"); return { ok: false, status: 401, data: null }; }
            let body = null;
            try { body = await response.json(); } catch { /* empty body */ }
            return { ok: response.ok, status: response.status, data: body };
        } catch (err) {
            setCurrentError(err.message);
            return { ok: false, status: 0, data: null };
        } finally {
            if (message) setCurrentLoading(false);
        }
    };

    var api = {
        getBooking: async () => {
            return await get('/api/getbooking', null, false, "Loading booking...");
        },
        status: async () => {
            return await get('/api/status', null, false, "Checking status...");
        },
        login: async (bookingReference, surname) => {
            const queryString = new URLSearchParams();
            var data = {
            };
            queryString.append("bookingref", bookingReference);
            queryString.append("surname", surname);
            return await post('/api/login', queryString, data, true, "Logging in...", false);
        },
        generatePaymentToken: async (amount, details) => {
            return await post('/api/payments/generatetoken', null, { amount, ...details }, false, "Generating payment...");
        },
        getCountries: async () => {
            return await get('/api/countries', null, true);
        },
         processPayment: async (token) => {
            return await post('/api/payments/processpayment', null, { token }, true, "Processing payment...");
        },
        logout: async () => {
            return await post('/api/logout', null, {}, true, "Logging out...");
        },
        carHireEligibility: async () => {
            return await get('/api/carhire/eligibility', null, true);
        },
        // Agent incentive card (placing agent only). Optional extra: degrade silently on failure
        // rather than raising the global error modal.
        getIncentive: async () => {
            const res = await send("GET", '/api/incentive');
            return res.ok ? res.data : null;
        },
        claimIncentive: async ({ agentName, agentEmail }) => {
            return await post('/api/incentive/claim', null, { agentName, agentEmail }, false, "Claiming your incentive...", false);
        },
        carHireGuests: async () => {
            // Ancillary (insurance lead names / guest pickers) — degrade silently on failure
            // rather than raising the global error modal.
            const res = await send("GET", '/api/carhire/guests');
            return res.ok ? res.data : [];
        },
        carHireSearch: async (body) => {
            return await post('/api/carhire/search', null, body || {}, false, "Searching for car hire...");
        },
        carHireDepots: async () => {
            return await get('/api/carhire/depots', null, true);
        },
        carHireExtras: async (token) => {
            const queryString = new URLSearchParams();
            queryString.append("token", token);
            return await get('/api/carhire/extras', queryString, true);
        },
        carHireInsurance: async (request) => {
            return await post('/api/carhire/insurance', null, request, true);
        },
        carHirePrebook: async (request) => {
            return await post('/api/carhire/prebook', null, request, false, "Preparing your car hire...");
        },
        carHireBook: async (request) => {
            return await post('/api/carhire/book', null, request, false, "Adding car hire to your booking...");
        },
        excursionEligibility: async () => {
            return await get('/api/excursion/eligibility', null, true);
        },
        excursionRoute: async (from, to, profile = "foot") => {
            // Walking route hotel -> pick-up point for the map (silent: the map degrades without it).
            const queryString = new URLSearchParams({
                fromLat: from.latitude, fromLng: from.longitude, toLat: to.latitude, toLng: to.longitude, profile
            });
            return await get('/api/excursion/route', queryString, true);
        },
        excursionGuests: async () => {
            return await get('/api/excursion/guests', null, true);
        },
        excursionSearch: async (silent = false) => {
            // silent: the home page pre-fetches in the background; the shop shows its own inline
            // loading state, so the global overlay would only get in the way.
            return await post('/api/excursion/search', null, {}, silent, "Searching for excursions...", !silent);
        },
        excursionPrebook: async (request) => {
            return await post('/api/excursion/prebook', null, request, false, "Preparing your excursion...");
        },
        excursionBook: async (request) => {
            return await post('/api/excursion/book', null, request, false, "Adding excursion to your booking...");
        },
        // ── session basket (all return { ok, status, data }) ─────────────────────────────
        basketGet: async () => {
            return await send("GET", '/api/basket');
        },
        basketAddExcursion: async (request) => {
            return await send("POST", '/api/basket/excursion', request, "Adding to your basket...");
        },
        basketSetCar: async (request) => {
            return await send("PUT", '/api/basket/car', request, "Adding to your basket...");
        },
        basketSetCarExtras: async (extras) => {
            return await send("PUT", '/api/basket/car/extras', { extras });
        },
        basketRemoveLine: async (lineId) => {
            return await send("DELETE", `/api/basket/line/${lineId}`);
        },
        basketSetLineGuests: async (lineId, request) => {
            return await send("PUT", `/api/basket/line/${lineId}/guests`, request);
        },
        basketSetLinePickup: async (lineId, pickupPointId) => {
            return await send("PUT", `/api/basket/line/${lineId}/pickup`, { pickupPointId });
        },
        basketQuote: async (silent = false) => {
            // silent: checkout re-quotes automatically in the background (inline button state
            // communicates progress), so the global overlay would only flicker.
            return await send("POST", '/api/basket/quote', {}, silent ? null : "Confirming your prices...");
        },
        basketCheckout: async (request) => {
            return await send("POST", '/api/basket/checkout', request, "Completing your booking...");
        }
    }
    return api;
} 